import React from 'react';
import { css } from '@emotion/react';
import { CircularProgress, Grid } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { isObject } from 'lodash';
import Snackbar from '@/components/Snackbar';
import { PrimaryButton } from '@/styles/blocks';
import { Body1, Title, TabCard } from '../blocks';
import EmailSubscriptionRow from './EmailSubscriptionRow';
import AddSubscriptionModal from './modals/AddSubscriptionModal';
import RemoveEmailModal from './modals/RemoveEmailModal';
import { useEmailSubscriptions } from './hooks';

const EmailSubscriptions = ({ client, callbacks, ...props }) => {
    const {
        notification,
        setNotification,
        addModal,
        setAddModal,
        deleteModal,
        setDeleteModal,
        commSections,
        commOptions,
        commsLoading,
        commsError,
        emailGroups,
        loading,
        openAddModal,
        handleUpdateCommunications,
        handleDelete,
        handleAddEmailSubscription,
    } = useEmailSubscriptions(client);

    return (
        <TabCard
            css={css`
                padding: 2rem;
            `}
            {...props}
        >
            <Snackbar
                open={isObject(notification)}
                handleClose={() => setNotification(null)}
                {...(notification || {})}
            />

            <AddSubscriptionModal
                addModal={addModal}
                setAddModal={setAddModal}
                commSections={commSections}
                loading={loading}
                onConfirm={handleAddEmailSubscription}
            />

            <RemoveEmailModal
                deleteModal={deleteModal}
                setDeleteModal={setDeleteModal}
                loading={loading}
                onConfirm={handleDelete}
            />

            <Grid
                direction="column"
                css={css`
                    flex-wrap: nowrap;
                `}
            >
                <Grid item>
                    <Title>Email Subscriptions</Title>
                </Grid>
                <Grid
                    item
                    css={css`
                        margin-bottom: 2rem;
                    `}
                >
                    <Body1>
                        Add email addresses to subscribe to specific email notifications. When these emails are sent,
                        they will also be sent to the email addresses that are subscribed to the specified notification.
                    </Body1>
                </Grid>

                <Grid
                    item
                    css={css`
                        margin-top: 1.25rem;
                    `}
                >
                    {/* Without the communication list there is nothing to subscribe to, and the
                        picker inside the modal would come up empty. */}
                    <PrimaryButton onClick={openAddModal} disabled={commsLoading || !!commsError}>
                        Add Email Subscription
                    </PrimaryButton>
                </Grid>

                <Grid
                    item
                    css={css`
                        margin-top: 1.5rem;
                    `}
                >
                    {/* The rows are gated on the communication list: until it resolves, every
                        address would render "Subscribed to 0 notifications", because the ids on the
                        subscription rows cannot be matched to anything yet. */}
                    {commsError ? (
                        <Alert severity="error">
                            Could not load the notification list, so existing subscriptions cannot be shown. Refresh to
                            try again.
                        </Alert>
                    ) : commsLoading ? (
                        <CircularProgress size={24} />
                    ) : (
                        emailGroups.map((group, index) => (
                            <EmailSubscriptionRow
                                key={group.email}
                                bottomSpacing={index === emailGroups.length - 1}
                                group={group}
                                commOptions={commOptions}
                                loading={loading}
                                setDeleteModal={setDeleteModal}
                                handleUpdateCommunications={handleUpdateCommunications}
                            />
                        ))
                    )}
                </Grid>
            </Grid>
        </TabCard>
    );
};

export default EmailSubscriptions;
